/**
 * Putting a saved quote back in the cart so it can be changed.
 *
 * The cart is the only editor a quote has — quantities, units and delivery all
 * live there — so editing means loading the quote's lines back into it and
 * remembering which quote they came from. Saving again then raises the next
 * revision of that quote rather than starting an unrelated one.
 */
import { Cart, CartItem, normalizeCart } from "../common/models/cart";
import { persistShoppingCart } from "../cart/serverCart";
import { Quote } from "./api";

export const REVISING_KEY = "konoRevisingQuote";
/** Quote being bought (not revised), so checkout can honor its price hold. */
export const BUYING_QUOTE_KEY = "konoBuyingQuote";

/**
 * Fired once the quote's lines are in the cart. The cart listens: reopening a
 * quote from the side panel while already looking at the cart does not remount
 * it, so without this the page would keep showing the old contents.
 */
export const QUOTE_REOPENED_EVENT = "kono-quote-reopened";

export type BeingRevised = { id: string; reference: string };

export const reopenInCart = (
  quote: Pick<Quote, "id" | "reference" | "cartItems"> &
    Partial<Pick<Quote, "totalShippingOverride" | "customer" | "delivery">>,
  opts?: {
    /**
     * False when the quote is loaded to be BOUGHT, not changed: the cart then
     * shows a clean checkout with no "you are changing quote…" banner — a
     * customer following a pay link should see nothing but the payment step.
     */
    revising?: boolean;
  },
) => {
  // Buying: the checkout form greets the QUOTE's customer, not whoever raised
  // the quote. A signed-in viewer's own profile still wins where it has data —
  // these are only the fallback for accounts with an incomplete profile.
  const buyerDefaults =
    opts?.revising === false && quote.customer
      ? {
          name: quote.customer.name || undefined,
          company: quote.customer.company || undefined,
          email: quote.customer.email || undefined,
          phone: quote.customer.phone || undefined,
          // The receiving contact and delivery instructions an operator
          // confirmed on the quote's freight plan, for the customer to check.
          ...(quote.delivery
            ? {
                deliveryContact: {
                  company: quote.delivery.receiving.company || undefined,
                  name: quote.delivery.receiving.name || undefined,
                  phone: quote.delivery.receiving.phone || undefined,
                  email: quote.delivery.receiving.email || undefined,
                },
                deliveryInstructions: quote.delivery.instructions || undefined,
              }
            : {}),
        }
      : undefined;
  const cart: Cart = normalizeCart({
    cartItems: quote.cartItems as CartItem[],
    totalShippingOverride: quote.totalShippingOverride,
    checkoutDefaults: buyerDefaults,
  });
  persistShoppingCart(cart);
  if (opts?.revising === false) {
    localStorage.removeItem(REVISING_KEY);
    localStorage.setItem(
      BUYING_QUOTE_KEY,
      JSON.stringify({ id: quote.id, reference: quote.reference }),
    );
  } else {
    localStorage.removeItem(BUYING_QUOTE_KEY);
    localStorage.setItem(
      REVISING_KEY,
      JSON.stringify({ id: quote.id, reference: quote.reference }),
    );
  }
  window.dispatchEvent(new Event(QUOTE_REOPENED_EVENT));
};

export const quoteBeingRevised = (): BeingRevised | null => {
  try {
    const stored = localStorage.getItem(REVISING_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const stopRevising = () => localStorage.removeItem(REVISING_KEY);

export const quoteBeingBought = (): BeingRevised | null => {
  try {
    const stored = localStorage.getItem(BUYING_QUOTE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const stopBuyingQuote = () => localStorage.removeItem(BUYING_QUOTE_KEY);
